import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import searchUtils from 'helpers/search';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledEntitiesQueryBar from 'components/organisms/Bars/EntitiesQueryBar/EntitiesQueryBar.styles';

const EntitiesQueryBar = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'EntitiesQueryBar');

  const tableProvider = useTable();
  const fieldData = tableProvider.fieldData;
  const filterGroupDefinitions = tableProvider.filterGroupDefinitions;

  const handleReset = () => {
    const newQuery = searchUtils.clearQuery(tableProvider.listState.query);
    newQuery.advanced = newQuery.advanced && !newQuery.assistant;
    newQuery.version = newQuery.version + 1;

    tableProvider.listState.setSearch('');
    tableProvider.listState.setFilter((current) => {
      return utils.applyFilters(current, [
        {id: 'terms', value: null},
        {id: 'wizard', value: null},
        {id: 'examples', value: null},
        {id: 'similarity', value: null},
      ], ['terms', 'wizard', 'examples', 'similarity']);
    });
    tableProvider.listState.setQuery(newQuery);

    tableProvider.assistantState.clear();
  }

  const handleChange = (query, terms, wizard, examples, similarity) => {
    tableProvider.listState.setSearch('');
    tableProvider.listState.setFilter((current) => {
      return utils.applyFilters(current, [
        {id: 'terms', value: terms?.length > 0 ? terms : null},
        {id: 'wizard', value: wizard ? wizard : null},
        {id: 'examples', value: examples?.length > 0 ? examples : null},
        {id: 'similarity', value: similarity?.length > 0 ? similarity : null},
      ], ['terms', 'wizard', 'examples', 'similarity']);
    });
    tableProvider.listState.setQuery((current) => {
      if (!utils.isDefined(current)) {
        setTimeout(() => {
          tableProvider.assistantState.clear();
        });
      }
      return query;
    });
  }

  const [terms, wizard, examples, similarity] = useMemo(() => {
    const termsFilter = (tableProvider.listState.filter ?? []).find((f) => f.id === 'terms');
    const terms = searchUtils.searchTextToTerms(tableProvider.listState.search)
      .map((t) => t.exactMatch ? `"${t.value}"` : t.value)
      .concat(termsFilter ? (utils.toArray(termsFilter.value, true) ?? []) : []);

    const wizardFilter = (tableProvider.listState.filter ?? []).find((f) => f.id === 'wizard');
    const wizard = wizardFilter ? (utils.toArray(wizardFilter.value, true)?.[0] ?? '') : '';
    const examplesFilter = (tableProvider.listState.filter ?? []).find((f) => f.id === 'examples');
    const examples = examplesFilter ? (utils.toArray(examplesFilter.value, true) ?? []) : [];
    const similarityFilter = (tableProvider.listState.filter ?? []).find((f) => f.id === 'similarity');
    const similarity = similarityFilter ? (utils.toArray(similarityFilter.value, true)?.[0] ?? '') : null;

    return [terms, wizard, examples, similarity];
  }, [tableProvider.listState.search, tableProvider.listState.filter]);

  const count = tableProvider?.list?.meta?.resultsCount;
  const topIds = searchUtils.filterCount(tableProvider?.listState.query) === 0 ? [] :
    tableProvider?.list?.data?.slice?.(0, 3)?.map((d) => d[tableProvider.dataKey]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledEntitiesQueryBar ref={ref} {...innerProps}
                                 search={tableProvider.listState.search}
                                 terms={terms}
                                 wizard={wizard}
                                 examples={examples}
                                 similarity={similarity}
                                 initialised={tableProvider.initialised}
                                 query={tableProvider.listState.query}
                                 state={tableProvider.assistantState}
                                 isLoading={tableProvider.isLoading() || !utils.isDefined(tableProvider.list?.meta?.resultsCount)}
                                 filterGroups={filterGroupDefinitions}
                                 fieldData={fieldData}
                                 count={count}
                                 topIds={topIds}
                                 onReset={handleReset}
                                 onChange={handleChange} />
});

EntitiesQueryBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default EntitiesQueryBar;
